import { useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, CheckCircle2, Clock3, FileText, User } from 'lucide-react';
import { BACKEND_URL } from '../config/backendUrl';
import { resolveBackendUrl, settingsApi } from '../services/api';

const DEFAULT_BRANDING = {
  company_name: 'Document Portal',
  company_logo_url: '',
  primary_color: '#334155',
  secondary_color: '#475569',
  tagline: '',
  about: '',
  welcome_message: 'Complete your onboarding documents below.',
  support_email: '',
  support_phone: '',
};

const DEFAULT_LABELS = {
  documents: 'Documents',
  forms: 'Forms',
  credentials: 'Credentials',
};

export default function PortalPreviewPage() {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [labels, setLabels] = useState(DEFAULT_LABELS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Both endpoints are recruiter-authenticated and derive the tenant from
        // the current session (or the validated master-admin tenant context).
        const [companyResponse, labelsResponse] = await Promise.all([
          settingsApi.getCompany(),
          axios.get(`${BACKEND_URL}/api/settings/ui-labels/me`),
        ]);
        if (cancelled) return;
        setBranding((current) => ({ ...current, ...(companyResponse.data || {}) }));
        setLabels((current) => ({ ...current, ...(labelsResponse.data || {}) }));
      } catch {
        if (!cancelled) setError('Unable to load the portal preview. Please return to Settings and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-200/70 flex items-center justify-center" data-testid="portal-preview-loading">
        <div className="w-8 h-8 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-200/70 flex items-center justify-center p-4" data-testid="portal-preview-error">
        <div className="max-w-md rounded-xl bg-white border p-6 text-center shadow-sm">
          <h1 className="font-semibold text-slate-800">Preview unavailable</h1>
          <p className="mt-2 text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  const primaryColor = branding.primary_color || DEFAULT_BRANDING.primary_color;
  const secondaryColor = branding.secondary_color || DEFAULT_BRANDING.secondary_color;
  const brandGradient = `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`;

  return (
    <div className="min-h-screen flex flex-col bg-slate-200/70" data-testid="portal-preview-page">
      <div className="bg-slate-900 text-white text-center text-xs sm:text-sm py-2 px-4" data-testid="portal-preview-banner">
        Portal preview — sample data only. Candidate actions are disabled.
      </div>

      <div className="h-2 flex shrink-0" aria-hidden="true">
        <div className="flex-1" style={{ backgroundColor: primaryColor }} />
        <div className="flex-1" style={{ backgroundColor: secondaryColor }} />
      </div>

      <header className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-5 sm:py-6">
          <div className="flex items-center gap-4 flex-wrap justify-center sm:justify-start">
            {branding.company_logo_url ? (
              <img
                src={resolveBackendUrl(branding.company_logo_url)}
                alt={branding.company_name || 'Company logo'}
                className="h-12 sm:h-16 max-w-[180px] object-contain"
                data-testid="portal-preview-logo"
              />
            ) : (
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: primaryColor }}>
                <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
            )}
            <div className="text-center sm:text-left">
              <h1 className="text-lg sm:text-xl font-bold text-slate-800" data-testid="portal-preview-company-name">
                {branding.company_name || DEFAULT_BRANDING.company_name}
              </h1>
              {branding.tagline && <p className="text-xs sm:text-sm text-slate-500 mt-0.5">{branding.tagline}</p>}
            </div>
          </div>
          {branding.about && <p className="text-xs sm:text-sm text-slate-500 mt-3 max-w-2xl whitespace-pre-line">{branding.about}</p>}
        </div>
      </header>

      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <p className="text-sm text-slate-500 whitespace-pre-line">
            {branding.welcome_message || DEFAULT_BRANDING.welcome_message}
          </p>
        </div>
      </div>

      <div className="bg-white border-b px-4 py-2.5 shadow-sm">
        <div className="max-w-4xl mx-auto flex items-center gap-2 text-sm">
          <User className="w-4 h-4 text-slate-400" />
          <span className="font-medium">Example Candidate</span>
          <span className="hidden sm:inline text-xs text-slate-500">(preview@example.com)</span>
        </div>
      </div>

      <main className="flex-1 px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-4">
          <section className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold">Your Progress</h2>
              <span className="text-sm text-slate-500">1 of 3 completed</span>
            </div>
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden mb-4">
              <div className="h-full w-1/3 rounded-full" style={{ background: brandGradient }} />
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <PreviewStat value="1" label="Pending" className="bg-blue-50 text-blue-600" />
              <PreviewStat value="1" label="In Progress" className="bg-amber-50 text-amber-600" />
              <PreviewStat value="1" label="Complete" className="bg-emerald-50 text-emerald-600" />
            </div>
          </section>

          <section className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border" data-testid="portal-preview-documents">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="font-semibold">{labels.documents}</h2>
                <p className="text-xs text-slate-500">Example items show how the candidate portal will look.</p>
              </div>
            </div>
            <div className="space-y-3">
              <PreviewItem icon={Clock3} title="New Starter Information" status="In progress" color="text-amber-600 bg-amber-50" />
              <PreviewItem icon={FileText} title="Employment Agreement" status="Pending" color="text-blue-600 bg-blue-50" />
              <PreviewItem icon={CheckCircle2} title="Privacy Notice" status="Completed" color="text-emerald-600 bg-emerald-50" />
            </div>
          </section>

          <div className="grid sm:grid-cols-2 gap-4">
            <PreviewCategory title={labels.forms} text="Online forms assigned to the candidate appear here." />
            <PreviewCategory title={labels.credentials} text="Qualification and credential requests appear here." />
          </div>
        </div>
      </main>

      {(branding.support_email || branding.support_phone) && (
        <footer className="bg-white border-t px-4 py-4 text-center text-xs text-slate-500">
          Need help? {[branding.support_email, branding.support_phone].filter(Boolean).join(' · ')}
        </footer>
      )}
    </div>
  );
}

function PreviewStat({ value, label, className }) {
  return (
    <div className={`p-2 rounded-lg ${className}`}>
      <p className="text-lg sm:text-2xl font-bold">{value}</p>
      <p className="text-xs opacity-70">{label}</p>
    </div>
  );
}

function PreviewItem({ icon: Icon, title, status, color }) {
  return (
    <div className="flex items-center justify-between gap-3 border rounded-xl p-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-sm font-medium text-slate-700 truncate">{title}</span>
      </div>
      <span className="text-xs text-slate-500 shrink-0">{status}</span>
    </div>
  );
}

function PreviewCategory({ title, text }) {
  return (
    <section className="bg-white rounded-2xl p-4 shadow-sm border">
      <h2 className="font-semibold text-sm">{title}</h2>
      <p className="text-xs text-slate-500 mt-1">{text}</p>
    </section>
  );
}
